<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import type { NotivueItem, TabIndexValue } from 'notivue';
import UiToast from '@krafta/ui-kit/components/UiToast.vue';

import type { AppNotificationRenderProps } from '@shared/notifications/appNotificationTypes';

const props = defineProps<{
  item: NotivueItem<AppNotificationRenderProps>;
  elementsTabIndex: TabIndexValue;
  streamPaused: boolean;
}>();

const isLoading = computed(() => props.item.type === 'promise');
const action = computed(() => props.item.props.action);
const actionPending = shallowRef(false);
const repeatCount = computed(() =>
  Math.max(
    props.item.props.repeatCount,
    props.item.duplicateCount + 1
  )
);
const progressDuration = computed(() =>
  Number.isFinite(props.item.duration) ? props.item.duration : undefined
);
const progressKey = computed(
  () => `${props.item.type}:${props.item.duplicateCount}`
);
const interactiveTabindex = computed(() => {
  const controlCount =
    (action.value ? 1 : 0) +
    (props.item.props.closable && !isLoading.value ? 1 : 0);

  if (controlCount === 0) return undefined;
  return controlCount === 1 ? 0 : props.elementsTabIndex;
});
const accessibilityProps = computed(() => ({
  ariaLive: props.item.props.ariaLive
}));

const clearItem = () => {
  props.item.props.onBeforeClear?.();
  props.item.clear();
};

const runAction = async () => {
  const currentAction = action.value;
  if (!currentAction || actionPending.value) return;

  actionPending.value = true;
  try {
    await currentAction.run();
    if (currentAction.shouldClose?.() ?? true) {
      clearItem();
    }
  } catch (error) {
    props.item.props.onActionError?.(error);
  } finally {
    actionPending.value = false;
  }
};
</script>

<template>
  <UiToast
    :key="progressKey"
    :tone="item.props.tone"
    :state="isLoading ? 'loading' : 'idle'"
    :title="item.props.title"
    :message="item.props.message"
    :reasons="item.props.reasons"
    :hidden-reason-count="item.props.hiddenReasonCount"
    :repeat-count="repeatCount"
    :action-label="action?.label"
    :action-busy="actionPending"
    :closable="item.props.closable && !isLoading"
    :role="item.props.role"
    v-bind="accessibilityProps"
    :progress-duration="progressDuration"
    :progress-paused="streamPaused"
    :interactive-tabindex="interactiveTabindex"
    @action="runAction"
    @close="clearItem"
  />
</template>
